import { BASE_LAYER, type DrawElement, type LayerElement, type PageElement, type SepiaElement } from "../../shared/model";

/** Where a change came from. Only "local" changes are sent to the server and recorded for undo. */
export type Origin = "local" | "remote" | "load" | "history";

export interface Patch {
  puts?: SepiaElement[];
  dels?: string[];
}

export interface Change {
  ids: Set<string>;
  origin: Origin;
  /** an element was added, removed or re-ordered */
  structural: boolean;
  /** previous state of every touched element (null = did not exist) */
  before: Map<string, SepiaElement | null>;
}

type Listener = (c: Change) => void;

function byZ(a: SepiaElement, b: SepiaElement): number {
  return a.z < b.z ? -1 : a.z > b.z ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** The elements of one board, in paint order, with change notifications. Elements are never mutated in place. */
export class Doc {
  private els = new Map<string, SepiaElement>();
  private ordered: DrawElement[] | null = null;
  private layerList: LayerElement[] | null = null;
  private pageList: PageElement[] | null = null;
  private zRange: { min: string | null; max: string | null } | null = null;
  private listeners = new Set<Listener>();
  private elListeners = new Map<string, Set<() => void>>();
  version = 0;
  structureVersion = 0;

  get(id: string): SepiaElement | undefined {
    return this.els.get(id);
  }

  has(id: string): boolean {
    return this.els.has(id);
  }

  get size(): number {
    return this.els.size;
  }

  /** Layers stored on the board, bottom to top (a board that never used layers has none: everything is on the base layer). */
  layers(): LayerElement[] {
    if (!this.layerList) {
      const out: LayerElement[] = [];
      for (const e of this.els.values()) if (e.type === "layer") out.push(e);
      this.layerList = out.sort(byZ);
    }
    return this.layerList;
  }

  /** The pages of a paged board, top to bottom (an endless board has none). */
  pages(): PageElement[] {
    if (!this.pageList) {
      const out: PageElement[] = [];
      for (const e of this.els.values()) if (e.type === "page") out.push(e);
      this.pageList = out.sort((a, b) => a.y - b.y || a.x - b.x || (a.id < b.id ? -1 : 1));
    }
    return this.pageList;
  }

  /** Every element including layers and pages (for storage and sync). */
  everything(): SepiaElement[] {
    return [...this.els.values()];
  }

  /**
   * Everything that is drawn, back to front: layer by layer (bottom layer first), and by z within a layer.
   * Elements on a missing layer count as the base layer. Cached until the next structural change: don't mutate it.
   */
  all(): DrawElement[] {
    if (!this.ordered) {
      const rank = new Map<string, number>();
      this.layers().forEach((l, i) => rank.set(l.id, i));
      const baseRank = rank.get(BASE_LAYER) ?? -1;
      const r = (e: DrawElement) => rank.get(e.layer || BASE_LAYER) ?? baseRank;
      const draw: DrawElement[] = [];
      for (const e of this.els.values()) if (e.type !== "layer" && e.type !== "page") draw.push(e);
      this.ordered = draw.sort((a, b) => r(a) - r(b) || byZ(a, b));
    }
    return this.ordered;
  }

  /** Highest and lowest z keys used by drawn elements, across all layers. */
  private range() {
    if (!this.zRange) {
      let min: string | null = null;
      let max: string | null = null;
      for (const e of this.els.values()) {
        if (e.type === "layer" || e.type === "page") continue;
        if (max === null || e.z > max) max = e.z;
        if (min === null || e.z < min) min = e.z;
      }
      this.zRange = { min, max };
    }
    return this.zRange;
  }

  /** New elements get keys above every existing key, so they land on top of whichever layer they join. */
  topZ(): string | null {
    return this.range().max;
  }

  bottomZ(): string | null {
    return this.range().min;
  }

  apply(patch: Patch, origin: Origin): Change | null {
    const before = new Map<string, SepiaElement | null>();
    let structural = false;
    let layersChanged = false;
    let pagesChanged = false;
    for (const el of patch.puts || []) {
      const prev = this.els.get(el.id) ?? null;
      if (prev === el) continue;
      if (!before.has(el.id)) before.set(el.id, prev);
      if (!prev || prev.z !== el.z || (prev.layer || null) !== (el.layer || null)) structural = true;
      // any change to a layer (visibility, order, opacity...) changes how the board is painted, and so does any page
      if (el.type === "layer" || prev?.type === "layer") structural = layersChanged = true;
      if (el.type === "page" || prev?.type === "page") structural = pagesChanged = true;
      this.els.set(el.id, el);
    }
    for (const id of patch.dels || []) {
      const prev = this.els.get(id);
      if (!prev) continue;
      if (!before.has(id)) before.set(id, prev);
      this.els.delete(id);
      structural = true;
      if (prev.type === "layer") layersChanged = true;
      if (prev.type === "page") pagesChanged = true;
    }
    if (!before.size) return null;
    this.version++;
    if (layersChanged) this.layerList = null;
    if (pagesChanged) this.pageList = null;
    if (structural) {
      this.structureVersion++;
      this.ordered = null;
      this.zRange = null;
    } else if (this.ordered) {
      // same order: refresh changed entries in the cached array
      const arr = this.ordered;
      for (let i = 0; i < arr.length; i++) {
        const cur = this.els.get(arr[i].id);
        if (cur && cur !== arr[i]) arr[i] = cur as DrawElement;
      }
      this.ordered = arr.slice();
    }
    const change: Change = { ids: new Set(before.keys()), origin, structural, before };
    for (const l of this.listeners) l(change);
    for (const id of change.ids) {
      const set = this.elListeners.get(id);
      if (set) for (const l of set) l();
    }
    return change;
  }

  /** Replace everything (used when a board is loaded). */
  reset(els: SepiaElement[], origin: Origin = "load"): void {
    const dels = [...this.els.keys()].filter((id) => !els.some((e) => e.id === id));
    this.apply({ puts: els, dels }, origin);
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  subscribeElement(id: string, fn: () => void): () => void {
    let set = this.elListeners.get(id);
    if (!set) {
      set = new Set();
      this.elListeners.set(id, set);
    }
    set.add(fn);
    return () => {
      set!.delete(fn);
      if (!set!.size) this.elListeners.delete(id);
    };
  }
}

// ---- undo / redo ----

interface Entry {
  before: Map<string, SepiaElement | null>;
  after: Map<string, SepiaElement | null>;
  key?: string;
  time: number;
  selection?: string[];
}

export class History {
  private undoStack: Entry[] = [];
  private redoStack: Entry[] = [];
  private listeners = new Set<() => void>();
  limit = 300;

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Record a change. Consecutive changes with the same merge key within 1.2 s become one undo step. */
  record(change: Change, doc: Doc, key?: string, selection?: string[]): void {
    const after = new Map<string, SepiaElement | null>();
    for (const id of change.ids) after.set(id, doc.get(id) ?? null);
    const last = this.undoStack[this.undoStack.length - 1];
    const now = performance.now();
    if (key && last && last.key === key && now - last.time < 1200) {
      for (const [id, prev] of change.before) if (!last.before.has(id)) last.before.set(id, prev);
      for (const [id, el] of after) last.after.set(id, el);
      last.time = now;
    } else {
      this.undoStack.push({ before: new Map(change.before), after, key, time: now, selection });
      if (this.undoStack.length > this.limit) this.undoStack.shift();
    }
    this.redoStack = [];
    this.emit();
  }

  /** Stop merging into the previous entry (e.g. after a pointer-up). */
  seal(): void {
    const last = this.undoStack[this.undoStack.length - 1];
    if (last) last.key = undefined;
  }

  undo(doc: Doc): { ids: string[]; selection?: string[] } | null {
    const e = this.undoStack.pop();
    if (!e) return null;
    this.redoStack.push(e);
    doc.apply(toPatch(e.before), "history");
    this.emit();
    return { ids: [...e.before.keys()].filter((id) => e.before.get(id)), selection: e.selection };
  }

  redo(doc: Doc): { ids: string[] } | null {
    const e = this.redoStack.pop();
    if (!e) return null;
    this.undoStack.push(e);
    doc.apply(toPatch(e.after), "history");
    this.emit();
    return { ids: [...e.after.keys()].filter((id) => e.after.get(id)) };
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.emit();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }
}

function toPatch(states: Map<string, SepiaElement | null>): Patch {
  const puts: SepiaElement[] = [];
  const dels: string[] = [];
  for (const [id, el] of states) {
    if (el) puts.push(el);
    else dels.push(id);
  }
  return { puts, dels };
}
